import { useState, useEffect } from 'react';

type LoadingPageProps = {
  initialStep?: number;
  totalSteps?: number;
  autoComplete?: boolean;
  onComplete?: () => void;
  stepDurations?: number[];
  retailerName: string;
};

const STEPS = [
  { number: 1, label: 'Pulling your e-receipts' },
  { number: 2, label: 'Finalizing connection' },
  { number: 3, label: 'Complete' },
];

export function LoadingPage({
  initialStep = 1,
  totalSteps = 3,
  autoComplete = true,
  onComplete,
  stepDurations = [2000, 3000, 1000],
  retailerName,
}: LoadingPageProps) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [progress, setProgress] = useState((initialStep / totalSteps) * 100);

  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  useEffect(() => {
    let stepTimeout: NodeJS.Timeout;

    const animateProgress = () => {
      const targetProgress = (currentStep / totalSteps) * 100;
      setProgress((prev) => {
        const diff = targetProgress - prev;
        if (Math.abs(diff) > 1) return prev + diff * 0.1;
        return targetProgress;
      });
    };

    const progressInterval = setInterval(animateProgress, 50);

    if (autoComplete && currentStep < totalSteps) {
      const duration = stepDurations[currentStep - 1] || 2000;
      stepTimeout = setTimeout(
        () => setCurrentStep((prev) => prev + 1),
        duration
      );
    } else if (autoComplete && currentStep === totalSteps && onComplete) {
      stepTimeout = setTimeout(() => onComplete(), 1500);
    }

    return () => {
      clearInterval(progressInterval);
      clearTimeout(stepTimeout);
    };
  }, [currentStep, totalSteps, autoComplete, onComplete, stepDurations]);

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    return 'pending';
  };

  const getStepMessage = () => {
    switch (currentStep) {
      case 1:
        return `Connecting to ${retailerName}...`;
      default:
        return 'Almost done...';
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 1:
        return 'Establishing a secure connection';
      case 2:
        return 'Finalizing your connection';
      default:
        return 'Wrapping up';
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className="flex flex-col items-center text-center px-6">
        {/* Pulse ring */}
        <div className="pulse-ring mb-8">
          <div className="pulse-ring-spinner" />
          <svg
            className="w-9 h-9 relative z-10"
            style={{ color: 'var(--retailer-primary)' }}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="font-display text-[22px] font-bold text-ink tracking-[-0.02em] mb-1.5">
          {getStepMessage()}
        </h1>
        <p className="text-[15px] text-ink-faint mb-7">{getStepSubtitle()}</p>

        {/* Progress bar */}
        <div className="w-[240px] h-[4px] bg-rule-light rounded-full overflow-hidden mb-10">
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-500 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Checklist */}
        <div className="flex flex-col w-[240px]">
          {STEPS.map((step, index) => {
            const status = getStepStatus(step.number);
            const isLast = index === STEPS.length - 1;

            return (
              <div
                key={step.number}
                className="flex items-start gap-3 relative"
              >
                {/* Connector */}
                {!isLast && (
                  <div
                    className="absolute left-[11px] top-[24px] bottom-[-4px] w-[1.5px] transition-colors duration-300"
                    style={{
                      background:
                        status === 'completed'
                          ? 'var(--retailer-primary)'
                          : 'var(--retailer-rule-light)',
                    }}
                  />
                )}

                {/* Dot */}
                <div
                  className={`w-6 h-6 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all duration-300 relative z-10 ${
                    status === 'completed'
                      ? 'border-primary bg-primary'
                      : status === 'active'
                        ? 'border-primary bg-primary-pale'
                        : 'border-rule bg-surface'
                  }`}
                >
                  {status === 'completed' && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                  {status === 'active' && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                  {status === 'pending' && (
                    <svg
                      className="w-3 h-3 text-rule"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>

                {/* Label */}
                <span
                  className={`text-[14px] pb-[10px] transition-all duration-300 ${
                    status === 'completed'
                      ? 'text-ink-muted'
                      : status === 'active'
                        ? 'text-ink font-semibold'
                        : 'text-ink-faint'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
